import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useStore } from "../contexts/StoreContext";
import { useLanguage } from "../contexts/LanguageContext";
import { fetchIntegrations } from "../api/queries";

/**
 * Custom hook for managing integrations data
 *
 * Responsibilities:
 * - Fetching integrations data
 * - Managing state for integrations
 * - Providing handlers for integration operations
 */
export const useIntegrationsData = () => {
  const { selectedStore } = useStore();
  const { t } = useLanguage();

  // UI State
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [integrationType, setIntegrationType] = useState(null);

  // Integration Data
  const [twilioData, setTwilioData] = useState(null);
  const [sendgridData, setSendgridData] = useState(null);
  const [mailgunData, setMailgunData] = useState(null);
  const [mailchimpData, setMailchimpData] = useState(null);
  const [mandrillData, setMandrillData] = useState(null);
  const [brevoData, setBrevoData] = useState(null);
  const [shopifyData, setShopifyData] = useState(null);
  const [webhookData, setWebhookData] = useState(null);
  const [bookingkitData, setBookingkitData] = useState(null);

  // Data Fetching - Integrations
  const {
    data: integrations = [],
    isLoading: isIntegrationsLoading,
    refetch,
  } = useQuery({
    queryKey: ["integrations", selectedStore?.id],
    queryFn: () => fetchIntegrations(selectedStore?.id),
    enabled: !!selectedStore?.id,
  });

  // Parse integrations data
  useEffect(() => {
    if (integrations?.length > 0) {
      const twilioIntegration = integrations.find(
        (integration) => integration.integration_type === "twilio",
      );
      if (twilioIntegration) {
        setTwilioData({
          id: twilioIntegration.id,
          accountSid: twilioIntegration.config["accountSid"],
          authToken: twilioIntegration.config["authToken"],
        });
      } else {
        setTwilioData(null);
      }

      const sendgridIntegration = integrations.find(
        (integration) => integration.integration_type === "sendgrid",
      );
      if (sendgridIntegration) {
        setSendgridData({
          id: sendgridIntegration.id,
          apiKey: sendgridIntegration.config["apiKey"],
        });
      } else {
        setSendgridData(null);
      }

      const mailgunIntegration = integrations.find(
        (integration) => integration.integration_type === "mailgun",
      );
      if (mailgunIntegration) {
        setMailgunData({
          id: mailgunIntegration.id,
          apiKey: mailgunIntegration.config["apiKey"],
          apiUrl: mailgunIntegration.config["apiUrl"],
        });
      } else {
        setMailgunData(null);
      }

      const mailchimpIntegration = integrations.find(
        (integration) => integration.integration_type === "mailchimp",
      );
      if (mailchimpIntegration) {
        setMailchimpData({
          id: mailchimpIntegration.id,
          apiKey: mailchimpIntegration.config["apiKey"],
        });
      } else {
        setMailchimpData(null);
      }

      const mandrillIntegration = integrations.find(
        (integration) => integration.integration_type === "mandrill",
      );
      if (mandrillIntegration) {
        setMandrillData({
          id: mandrillIntegration.id,
          apiKey: mandrillIntegration.config["apiKey"],
        });
      } else {
        setMandrillData(null);
      }

      const brevoIntegration = integrations.find(
        (integration) => integration.integration_type === "brevo",
      );
      if (brevoIntegration) {
        setBrevoData({
          id: brevoIntegration.id,
          apiKey: brevoIntegration.config["apiKey"],
        });
      } else {
        setBrevoData(null);
      }

      const shopifyIntegration = integrations.find(
        (integration) => integration.integration_type === "shopify",
      );
      if (shopifyIntegration) {
        setShopifyData({
          id: shopifyIntegration.id,
          adminApiAccessToken: shopifyIntegration.config["adminApiAccessToken"],
          shopDomain: shopifyIntegration.config["shopDomain"],
        });
      } else {
        setShopifyData(null);
      }

      const webhookIntegration = integrations.find(
        (integration) => integration.integration_type === "webhook",
      );
      if (webhookIntegration) {
        setWebhookData({
          id: webhookIntegration.id,
          url: webhookIntegration.config["url"],
          events: webhookIntegration.config["events"] || [],
        });
      } else {
        setWebhookData(null);
      }

      const bookingkitIntegration = integrations.find(
        (integration) => integration.integration_type === "bookingkit",
      );
      if (bookingkitIntegration) {
        setBookingkitData({
          id: bookingkitIntegration.id,
          passId: bookingkitIntegration.config["passId"],
          allowedVendorIds:
            bookingkitIntegration.config["allowedVendorIds"] || [],
        });
      } else {
        setBookingkitData(null);
      }
    } else {
      setTwilioData(null);
      setSendgridData(null);
      setMailgunData(null);
      setMailchimpData(null);
      setMandrillData(null);
      setBrevoData(null);
      setShopifyData(null);
      setWebhookData(null);
      setBookingkitData(null);
    }
  }, [integrations]);

  // Handlers
  const handleTwilioConnect = () => {
    setIntegrationType(1);
    setIsModalOpen(true);
  };

  const handleSendgridConnect = () => {
    setIntegrationType(2);
    setIsModalOpen(true);
  };

  const handleMailchimpConnect = () => {
    setIntegrationType(3);
    setIsModalOpen(true);
  };

  const handleMailgunConnect = () => {
    setIntegrationType(4);
    setIsModalOpen(true);
  };

  const handleMandrillConnect = () => {
    setIntegrationType(11);
    setIsModalOpen(true);
  };

  const handleBrevoConnect = () => {
    setIntegrationType(10);
    setIsModalOpen(true);
  };

  const handleShopifyConnect = () => {
    setIntegrationType(7);
    setIsModalOpen(true);
  };

  const handleWebhookConnect = () => {
    setIntegrationType(12);
    setIsModalOpen(true);
  };

  const handleBookingkitConnect = () => {
    setIntegrationType(13);
    setIsModalOpen(true);
  };

  const handleFalseConnect = () => {
    return false;
  };

  const handleIntegrationSuccess = () => {
    refetch();
  };

  // Integration list with statuses
  const integrationList = [
    {
      id: twilioData?.id,
      key: "twilio",
      title: "Twilio",
      tags: ["sms"],
      status: twilioData ? "connected" : "available",
      onClick: handleTwilioConnect,
      createApiKeyLink: "https://docs.loyaltify.io/en/articles/11460052-twilio",
    },
    {
      id: sendgridData?.id,
      key: "sendgrid",
      title: "SendGrid",
      tags: ["email"],
      status: sendgridData ? "connected" : "available",
      onClick: handleSendgridConnect,
      createApiKeyLink:
        "https://docs.loyaltify.io/en/articles/11460054-sendgrid",
    },
    {
      id: mailchimpData?.id,
      key: "mailchimp",
      title: "Mailchimp",
      tags: ["crm"],
      status: mailchimpData ? "connected" : "available",
      onClick: handleMailchimpConnect,
      createApiKeyLink:
        "https://docs.loyaltify.io/en/articles/11460055-mailchimp",
    },
    {
      id: mailgunData?.id,
      key: "mailgun",
      title: "Mailgun",
      tags: ["email"],
      status: mailgunData ? "connected" : "available",
      onClick: handleMailgunConnect,
      createApiKeyLink:
        "https://docs.loyaltify.io/en/articles/11460057-mailgun",
    },
    {
      id: 5,
      key: "stripe",
      title: "Stripe",
      tags: ["payment"],
      status: "soon",
      onClick: handleFalseConnect,
    },
    // {
    //   id: 6,
    //   key: "paypal",
    //   title: "PayPal",
    //   tags: ['payment'],
    //   status: "soon",
    //   onClick: handleFalseConnect,
    // },
    {
      id: shopifyData?.id,
      key: "shopify",
      title: "Shopify Webhook",
      tags: ["e-commerce"],
      status: shopifyData ? "connected" : "available",
      onClick: handleShopifyConnect,
      createApiKeyLink:
        "https://docs.loyaltify.io/en/articles/11460064-shopify",
    },
    // {
    //   id: 8,
    //   key: "woocommerce",
    //   title: "WooCommerce",
    //   tags: ['e-commerce'],
    //   status: "soon",
    //   onClick: handleFalseConnect,
    // },
    // {
    //   id: 9,
    //   key: "klaviyo",
    //   title: "Klaviyo",
    //   tags: ['crm'],
    //   status: "soon",
    //   onClick: handleFalseConnect,
    // },
    // {
    //   id: 10,
    //   key: "brevo",
    //   title: "Brevo",
    //   tags: ["crm", "email", "sms"],
    //   status: "soon",
    //   onClick: handleFalseConnect,
    // },
    {
      id: mandrillData?.id,
      key: "mandrill",
      title: "Mandrill",
      tags: ["email"],
      status: mandrillData ? "connected" : "available",
      onClick: handleMandrillConnect,
      createApiKeyLink:
        "https://docs.loyaltify.io/en/articles/11460058-mandrill",
    },
    {
      id: brevoData?.id,
      key: "brevo",
      title: "Brevo",
      tags: ["crm"],
      status: brevoData ? "connected" : "available",
      onClick: handleBrevoConnect,
      createApiKeyLink: "https://docs.loyaltify.io/en/articles/11460059-brevo",
    },
    {
      id: webhookData?.id,
      key: "webhook",
      title: "Webhook",
      tags: ["automation"],
      status: webhookData ? "connected" : "available",
      onClick: handleWebhookConnect,
    },
    {
      id: bookingkitData?.id,
      key: "bookingkit",
      title: "BookingKit",
      tags: ["booking", "e-commerce"],
      status: bookingkitData ? "connected" : "available",
      onClick: handleBookingkitConnect,
    },
  ];

  return {
    // Data
    integrations,
    twilioData,
    sendgridData,
    mailgunData,
    mailchimpData,
    mandrillData,
    brevoData,
    shopifyData,
    webhookData,
    bookingkitData,
    integrationList,

    // UI State
    isModalOpen,
    setIsModalOpen,
    integrationType,
    setIntegrationType,

    // Loading State
    isIntegrationsLoading,

    // Store data
    storeId: selectedStore?.id,

    // Handlers
    handleTwilioConnect,
    handleSendgridConnect,
    handleMailchimpConnect,
    handleMailgunConnect,
    handleMandrillConnect,
    handleBrevoConnect,
    handleShopifyConnect,
    handleWebhookConnect,
    handleBookingkitConnect,
    handleIntegrationSuccess,
  };
};
